/**
 * Multi-language field per-field tab switching.
 *
 * HTML (generated by form templates):
 *   <div class="form-lang-container" data-lang-code="en"> ... </div>
 *   <div class="form-lang-container" data-lang-code="pt"> ... </div>
 *
 * Each language's fields (inputs, textareas, editors, etc.) live inside their
 * own .form-lang-container. The tab pills show/hide the container, so all
 * content inside — including Quill editors or any other plugin — is controlled
 * automatically without needing per-plugin awareness.
 *
 * Legacy fallback: if no .form-lang-container wrappers are found, falls back
 * to the old behaviour of toggling .form-control-translate fields directly.
 */

const HIDDEN_CLASS   = 'form-control-translate-hidden';
const CONTROLS_CLASS = 'translation-controls';

const LANG_FLAG = {
    'af':    '🇿🇦',
    'ar':    '🇸🇦',
    'bg':    '🇧🇬',
    'cs':    '🇨🇿',
    'da':    '🇩🇰',
    'de':    '🇩🇪',
    'el':    '🇬🇷',
    'en':    '🇬🇧',
    'es':    '🇪🇸',
    'es-ar': '🇦🇷',
    'es-mx': '🇲🇽',
    'et':    '🇪🇪',
    'fi':    '🇫🇮',
    'fr':    '🇫🇷',
    'he':    '🇮🇱',
    'hi':    '🇮🇳',
    'hr':    '🇭🇷',
    'hu':    '🇭🇺',
    'id':    '🇮🇩',
    'it':    '🇮🇹',
    'ja':    '🇯🇵',
    'ko':    '🇰🇷',
    'lt':    '🇱🇹',
    'lv':    '🇱🇻',
    'nl':    '🇳🇱',
    'no':    '🇳🇴',
    'pl':    '🇵🇱',
    'pt':    '🇵🇹',
    'pt-br': '🇧🇷',
    'ro':    '🇷🇴',
    'ru':    '🇷🇺',
    'sk':    '🇸🇰',
    'sl':    '🇸🇮',
    'sq':    '🇦🇱',
    'sr':    '🇷🇸',
    'sv':    '🇸🇪',
    'th':    '🇹🇭',
    'tr':    '🇹🇷',
    'uk':    '🇺🇦',
    'vi':    '🇻🇳',
    'zh':    '🇨🇳',
    'zh-tw': '🇹🇼',
};

let _langs = null;

const translate = {
    /**
     * Build per-field tab navs for all translatable fields on the page and
     * bind group lifecycle events so tabs are rebuilt after new rows are added.
     */
    init() {
        const hasContainers = !!document.querySelector('.form-lang-container[data-lang-code]');
        const hasLegacy     = !!document.querySelector('.form-control-translate[data-lang-code]');

        if (!hasContainers && !hasLegacy) {
            return;
        }

        _buildTabs(document);

        document.querySelectorAll('[data-group]:not(.translatelistening)').forEach(group => {
            group.addEventListener('nata:form:group:after:increment', (event) => {
                const { newRow } = event.detail;
                translate.resetLangs();
                _buildTabs(newRow);
            });
            group.classList.add('translatelistening');
        });
    },

    /**
     * Return {code: label} map discovered from visible lang containers/fields.
     * Cached after first call; reset with resetLangs().
     */
    langs() {
        if (_langs) {
            return _langs;
        }
        _langs = {};
        document.querySelectorAll('[data-lang-code]').forEach(el => {
            const code = el.dataset.langCode;
            if (!code || _langs[code]) {
                return;
            }
            // Prefer placeholder from an inner field; fall back to lang code
            const field = el.querySelector('[placeholder]') || el;
            if (field.placeholder) {
                _langs[code] = field.placeholder;
            } else if (el.placeholder) {
                _langs[code] = el.placeholder;
            } else {
                _langs[code] = code;
            }
        });
        return _langs;
    },

    resetLangs() {
        _langs = null;
    },

    /** Show a lang container (or legacy field) externally. */
    field: {
        show(el) { _showItem(_resolveItem(el)); },
        hide(el) { _hideItem(_resolveItem(el)); },
    }
};

// --- private helpers ---

/**
 * Resolve the visibility-controlling element for a given node.
 * If the node is inside a .form-lang-container, return the container.
 * Otherwise return the node itself (legacy).
 *
 * @param {HTMLElement} el
 * @return {HTMLElement}
 */
function _resolveItem(el) {
    const container = el.closest('.form-lang-container');
    return container || el;
}

/**
 * Group all language items within root by their common parent, then inject
 * a tab nav before each group.
 *
 * Detects .form-lang-container[data-lang-code] wrappers first.
 * Falls back to .form-control-translate[data-lang-code] fields for templates
 * that have not yet been updated with containers.
 *
 * @param {Document|HTMLElement} root
 */
function _buildTabs(root) {
    const groups = new Map();

    // Primary: container-based
    root.querySelectorAll('.form-lang-container[data-lang-code]').forEach(container => {
        const parent = container.parentElement;
        if (parent.querySelector(':scope > .' + CONTROLS_CLASS)) {
            return;
        }
        if (!groups.has(parent)) {
            groups.set(parent, []);
        }
        const list = groups.get(parent);
        if (!list.includes(container)) {
            list.push(container);
        }
    });

    // Legacy fallback: direct fields not inside a container
    root.querySelectorAll('.form-control-translate[data-lang-code]').forEach(field => {
        if (field.closest('.form-lang-container')) {
            return;
        }
        const parent = field.parentElement;
        if (parent.querySelector(':scope > .' + CONTROLS_CLASS)) {
            return;
        }
        if (!groups.has(parent)) {
            groups.set(parent, []);
        }
        const list = groups.get(parent);
        if (!list.includes(field)) {
            list.push(field);
        }
    });

    const currentLang = _currentLang();

    groups.forEach((items, parent) => {
        if (!items.length) {
            return;
        }

        const activeLang = items.some(i => i.dataset.langCode === currentLang)
            ? currentLang
            : items[0].dataset.langCode;

        const nav = _buildTabNav(items, activeLang);
        parent.insertBefore(nav, items[0]);

        items.forEach(item => {
            if (item.dataset.langCode === activeLang) {
                _showItem(item);
            } else {
                _hideItem(item);
            }
        });
    });
}

/**
 * Build a .translation-controls div with one button per item.
 *
 * @param {HTMLElement[]} items  Lang containers or legacy fields
 * @param {string} activeLang
 * @return {HTMLElement}
 */
function _buildTabNav(items, activeLang) {
    const nav = document.createElement('div');
    nav.className = CONTROLS_CLASS;

    items.forEach(item => {
        const code = item.dataset.langCode;

        // Get the language label: from an inner field's placeholder, or the
        // item's own placeholder (legacy), or fall back to the lang code.
        const innerField = item.querySelector('[placeholder][data-lang-code="' + code + '"]')
            || item.querySelector('[placeholder]');
        const label = (innerField && innerField.placeholder)
            || (item.placeholder)
            || code;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.langCode = code;
        if (code === activeLang) {
            btn.classList.add('active');
        }

        const icon = document.createElement('span');
        icon.textContent = LANG_FLAG[code] || '🏳';
        btn.appendChild(icon);

        const span = document.createElement('span');
        span.className = 'language-name';
        span.textContent = '\u00a0' + label;
        btn.appendChild(span);

        btn.addEventListener('click', () => {
            nav.querySelectorAll('button').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            items.forEach(i => {
                if (i.dataset.langCode === code) {
                    _showItem(i);
                } else {
                    _hideItem(i);
                }
            });
        });

        nav.appendChild(btn);
    });

    return nav;
}

/**
 * Show a lang item (container or legacy field).
 *
 * @param {HTMLElement} item
 */
function _showItem(item) {
    item.classList.remove(HIDDEN_CLASS);

    // Legacy: also show a Quill sibling that may exist outside the item
    if (!item.classList.contains('form-lang-container')) {
        const qe = _quillSibling(item);
        if (qe) {
            qe.classList.remove(HIDDEN_CLASS);
        }
    }
}

/**
 * Hide a lang item (container or legacy field).
 *
 * @param {HTMLElement} item
 */
function _hideItem(item) {
    item.classList.add(HIDDEN_CLASS);

    // Legacy: also hide a Quill sibling that may exist outside the item
    if (!item.classList.contains('form-lang-container')) {
        const qe = _quillSibling(item);
        if (qe) {
            qe.classList.add(HIDDEN_CLASS);
        }
    }
}

/**
 * Return the .nata-quill-editor sibling following a textarea, or null.
 * Used only for the legacy (no-container) path.
 *
 * @param {HTMLElement} field
 * @return {HTMLElement|null}
 */
function _quillSibling(field) {
    if (field.tagName !== 'TEXTAREA') {
        return null;
    }
    let el = field.nextElementSibling;
    while (el) {
        if (el.classList.contains('nata-quill-editor')) {
            return el;
        }
        el = el.nextElementSibling;
    }
    return null;
}

/**
 * Return the current UI language code (e.g. "en", "pt").
 *
 * @return {string}
 */
function _currentLang() {
    if (window.nata && window.nata.i18n && typeof window.nata.i18n.getLanguage === 'function') {
        return window.nata.i18n.getLanguage();
    }
    return document.documentElement.lang || 'en';
}


export default translate;
